/**
 * GLDN Searchspring recs — add-to-cart handler
 *
 * gldn110 (#443): the Searchspring side-cart recommendation cards render
 * their own `<form class="add-to-cart-form">` with NO `action` and NO
 * `method` attribute — SnapUI expects the storefront to supply the submit
 * handler, and on this theme nothing ever did. A native submit on an
 * action-less form is a GET to the current URL with the variant serialized
 * as `?id=<variant>`, which is why clicking "Add" reloaded the page, closed
 * the cart drawer, and never added the product.
 *
 * Note this is NOT caused by the innerHTML clone in layout/theme.liquid that
 * copies these cards from the hidden `#siderec` mount into the drawer. The
 * original, un-cloned form in `#siderec` fails identically — there are no
 * event listeners on either copy. Re-parenting the recs does not fix it.
 *
 * Implementation: one delegated capture-phase `submit` listener on document,
 * scoped to action-less `.add-to-cart-form`s inside a Searchspring recs root.
 * It POSTs to /cart/add.js asking for the cart-items sections, then dispatches
 * the theme's `cart:update` event so `cart-items-component` morphs the drawer
 * in place and `cart-icon` updates the bubble. Nothing navigates, so the
 * drawer stays open.
 *
 * The `_unique` line-item property is added by gldn-cart-unique.js, which
 * patches window.fetch and already handles FormData bodies — do not add it
 * here or it will be double-injected.
 */
(function () {
  /* Both conditions must hold for us to claim a form:
     1. it lives inside a Searchspring recs mount, and
     2. it has no `action` attribute.
     Theme and app product forms are Shopify `{% form %}` tags and always
     carry an action, so this can never hijack a PDP / quick-add submit. */
  var SS_ROOTS = [
    '#siderec',
    '.ss__recs__side-cart',
    '#gldn-drawer-recs-target',
    '.gldn-drawer-recs',
    '.ss-recs',
    '[searchspring-recommend]',
  ].join(', ');

  /* Per-form, not global: a shopper clicking a second card while the first
     add is still in flight must not have their click silently swallowed. */
  var inFlight = new WeakSet();

  function isSearchspringRecsForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (!form.classList.contains('add-to-cart-form')) return false;
    if (form.hasAttribute('action')) return false;
    return !!form.closest(SS_ROOTS);
  }

  /* The cart-items sections currently on the page (drawer and/or cart page).
     Asking /cart/add.js to render them lets cart-items-component morph
     without a second round trip — same contract product-form.js uses. */
  function cartItemsSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.dataset ? el.dataset.sectionId : null;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function currentItemCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return cart && typeof cart.item_count === 'number' ? cart.item_count : null; })
      .catch(function () { return null; });
  }

  /* SnapUI's cloned cards live inside <cart-items-component>, so the section
     morph triggered by cart:update wipes them out of the drawer. The inline
     cloner in layout/theme.liquid re-clones on cart:update; give it a beat to
     run, then confirm. Exposed by that IIFE as window.gldnCloneRecsToDrawer. */
  function recloneRecs() {
    if (typeof window.gldnCloneRecsToDrawer === 'function') {
      window.gldnCloneRecsToDrawer();
    }
  }

  function onSubmit(event) {
    var form = event.target;
    if (!isSearchspringRecsForm(form)) return;

    event.preventDefault();
    event.stopPropagation();

    if (inFlight.has(form)) return;

    /* Build from the form itself so a multi-variant card (<select name="id">)
       works the same as the single hidden input SnapUI ships today. */
    var body = new FormData(form);
    var variantId = body.get('id');
    if (!variantId) return;
    if (!body.get('quantity')) body.set('quantity', '1');

    var button = form.querySelector('button, [type="submit"]');
    inFlight.add(form);
    if (button) button.setAttribute('disabled', 'disabled');

    var sectionIds = cartItemsSectionIds();
    if (sectionIds.length) {
      body.set('sections', sectionIds.join(','));
      body.set('sections_url', window.location.pathname);
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    })
      .then(function (response) { return response.json(); })
      .then(function (response) {
        /* Shopify returns a `status` field only on failure (e.g. sold out). */
        if (response && response.status) {
          /* Emit the theme's own cart:error so the shopper-facing surfaces and
             gldn-cart-atc-instrument's atc_sold_out / atc_error counters both
             see it. Console-only swallowed a real signal. */
          document.dispatchEvent(
            new CustomEvent('cart:error', {
              bubbles: true,
              detail: {
                sourceId: 'gldn-ss-recs-add-to-cart',
                data: {
                  message: response.message,
                  description: response.description,
                  errors: response.errors,
                },
              },
            })
          );
          return;
        }

        return currentItemCount().then(function (itemCount) {
          /* cart-icon does `itemCount ?? 0`, so a null/undefined count would
             BLANK the bubble on an otherwise successful add. When the count is
             unknown, fall back to the documented delta path instead: the
             'product-form-component' source makes cart-icon add to the current
             count rather than replace it. Absolute when known, delta when not. */
          var known = typeof itemCount === 'number';
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: response,
                sourceId: 'gldn-ss-recs-add-to-cart',
                data: {
                  source: known ? 'gldn-ss-recs-add-to-cart' : 'product-form-component',
                  itemCount: known ? itemCount : 1,
                  variantId: String(variantId),
                  sections: response ? response.sections : undefined,
                },
              },
            })
          );
          /* The morph above removes the cloned recs; restore them. */
          setTimeout(recloneRecs, 350);
          setTimeout(recloneRecs, 1200);
        });
      })
      .catch(function (error) {
        console.error('[gldn-ss-recs] add to cart failed:', error);
      })
      .finally(function () {
        inFlight.delete(form);
        if (button) button.removeAttribute('disabled');
      });
  }

  /* Capture phase so we preventDefault before any other handler can run.
     The recs markup is injected and re-injected by SnapUI (and cloned into
     the drawer), so this must be delegated on document rather than bound
     per form. */
  document.addEventListener('submit', onSubmit, true);
})();
